/**
 * Utility functions for managing keypoint display order
 * Allows custom ordering for UI while maintaining data structure
 */

import { Keypoint } from '../types'
import { KEYPOINT_ORDER_CONFIGS, DEFAULT_ORDER_CONFIG } from './keypointOrderConfig'
import { isKeypointUnset } from './keypointUtils'

/**
 * Get display order for keypoints
 * @param totalKeypoints Total number of keypoints
 * @param configName Name of the order configuration to use
 * @returns Array of indices in display order
 */
export function getKeypointDisplayOrder(
  keypointNames: string[],
  configName: string = DEFAULT_ORDER_CONFIG
): number[] {
  const config = KEYPOINT_ORDER_CONFIGS[configName] || KEYPOINT_ORDER_CONFIGS.default
  return config.orderFunction(keypointNames)
}

/**
 * Convert display index to data index
 * @param displayIndex Index in the displayed order
 * @param totalKeypoints Total number of keypoints
 * @returns Actual data index
 */
export function displayToDataIndex(displayIndex: number, keypointNames: string[]): number {
  const displayOrder = getKeypointDisplayOrder(keypointNames)
  return displayOrder[displayIndex]
}

/**
 * Convert data index to display index
 * @param dataIndex Actual data index
 * @param totalKeypoints Total number of keypoints
 * @returns Index in the displayed order
 */
export function dataToDisplayIndex(dataIndex: number, keypointNames: string[]): number {
  const displayOrder = getKeypointDisplayOrder(keypointNames)
  return displayOrder.indexOf(dataIndex)
}

/**
 * Find next unset keypoint in display order
 * @param keypoints Array of keypoints
 * @param currentDataIndex Current keypoint data index
 * @returns Next unset keypoint data index or -1
 */
export function findNextUnsetKeypointInDisplayOrder(
  keypoints: Keypoint[],
  currentDataIndex: number,
  keypointNames: string[]
): number {
  const totalKeypoints = keypointNames.length
  const currentDisplayIndex = dataToDisplayIndex(currentDataIndex, keypointNames)
  const displayOrder = getKeypointDisplayOrder(keypointNames)

  // Search from next display position
  for (let i = currentDisplayIndex + 1; i < totalKeypoints; i++) {
    const dataIndex = displayOrder[i]
    if (isKeypointUnset(keypoints[dataIndex])) {
      return dataIndex
    }
  }

  // Wrap around to beginning
  for (let i = 0; i <= currentDisplayIndex; i++) {
    const dataIndex = displayOrder[i]
    if (isKeypointUnset(keypoints[dataIndex])) {
      return dataIndex
    }
  }

  return -1
}

/**
 * Navigate to previous/next keypoint in display order
 * @param currentDataIndex Current keypoint data index
 * @param totalKeypoints Total number of keypoints
 * @param direction 'prev' or 'next'
 * @returns New keypoint data index
 */
export function navigateKeypointInDisplayOrder(
  currentDataIndex: number,
  keypointNames: string[],
  direction: 'prev' | 'next'
): number {
  const totalKeypoints = keypointNames.length
  const currentDisplayIndex = dataToDisplayIndex(currentDataIndex, keypointNames)

  if (direction === 'prev') {
    const newDisplayIndex = Math.max(0, currentDisplayIndex - 1)
    return displayToDataIndex(newDisplayIndex, keypointNames)
  } else {
    const newDisplayIndex = Math.min(totalKeypoints - 1, currentDisplayIndex + 1)
    return displayToDataIndex(newDisplayIndex, keypointNames)
  }
}
